import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "../lib/utils"

const inputVariants = cva(
  "flex w-full rounded-lg bg-background px-2 pr-1 py-3 text-body-lg font-normal placeholder:text-placeholder focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "min-h-12 focus:border focus:border-gray",
        thin: "h-9 min-h-9 text-body-sm focus:border focus:border-text",
        error: "min-h-12 border border-attention",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(inputVariants({ variant, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export interface InputFieldProps extends InputProps {
  label?: string
  message?: string
  error?: string
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  ({ className, label, message, error, variant, ...props }, ref) => {
    const fieldVariant = error ? "error" : variant

    return (
      <div className={cn("flex w-full flex-col gap-0.5", className)}>
        {label && (
          <div className="flex items-center justify-center px-2">
            <span className="flex-1 text-label-lg font-semibold text-text leading-[1.35]">
              {label}
            </span>
          </div>
        )}
        <Input ref={ref} variant={fieldVariant} {...props} />
        {(message || error) && (
          <div className="flex items-center justify-center px-2">
            <span
              className={cn(
                "flex-1 text-label-sm leading-[1.35]",
                error ? "font-semibold text-attention" : "font-normal text-gray"
              )}
            >
              {error || message}
            </span>
          </div>
        )}
      </div>
    )
  }
)
InputField.displayName = "InputField"

export { Input, InputField, inputVariants }
